import React, { useState, useCallback, useRef, useEffect } from 'react';
import useKeepAwake from '../hooks/useKeepAwake';
import KeepAwakeToggle from './KeepAwakeToggle';
import { Heart, Shield, Flame, Target, Sparkles, Leaf, ArrowLeft, RefreshCw, ImagePlus, Trash2, X, Camera, SlidersHorizontal, PlayCircle, Info, Volume2 } from 'lucide-react';
import { AFFIRMATION_THEMES } from '../data/affirmations';
import SessionAudioPanel from './SessionAudioPanel';
import AffirmationsAutoPlayer from './AffirmationsAutoPlayer';
import SensualVoiceGenerator from './SensualVoiceGenerator';
import AffirmationFolderUploader from './AffirmationFolderUploader';
import OrphanClipsBanner from './OrphanClipsBanner';
import useAffirmationClips from '../hooks/useAffirmationClips';
import useAffirmationSettings from '../hooks/useAffirmationSettings';
import useAppSettings from '../hooks/useAppSettings';
import { toast } from 'sonner';

const ICONS = { Heart, Shield, Flame, Target, Sparkles, Leaf };
const STORAGE_KEY = 'hypnose_aff_photos';
const MAX_USER_PHOTOS = 5;

const getUserPhotos = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch { return {}; }
};

const saveUserPhotos = (data) => localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

const AffirmationsPanel = ({ isOpen, onClose, isAdmin = false }) => {
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [currentAffirmation, setCurrentAffirmation] = useState('');
  const [currentAffirmationIdx, setCurrentAffirmationIdx] = useState(0);
  const [bgIndex, setBgIndex] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [showSessionAudio, setShowSessionAudio] = useState(false);
  const [autoPlayMode, setAutoPlayMode] = useState(null); // null | 'ludovic' | 'mon-choix' | 'sans-voix'
  const [showInfo, setShowInfo] = useState(false);
  const [showDownloads, setShowDownloads] = useState(false);
  const [userPhotos, setUserPhotos] = useState(getUserPhotos);
  const autoAudioRef = useRef(null);
  const autoTimerRef = useRef(null);
  const clipsApi = useAffirmationClips(selectedTheme?.id);
  const { clips: aClips, getClip } = clipsApi;
  const { settings, update: updateSettings } = useAffirmationSettings();
  const { settings: appSettings, update: updateAppSettings } = useAppSettings();
  const [allImages, setAllImages] = useState([]);
  const usedIndices = useRef([]);
  const fileRef = useRef(null);

  // Merge default + user photos for current theme
  useEffect(() => {
    if (selectedTheme) {
      const custom = userPhotos[selectedTheme.id] || [];
      setAllImages([...selectedTheme.images, ...custom]);
    }
  }, [selectedTheme, userPhotos]);

  const getRandomAffirmation = useCallback((theme) => {
    const aff = theme.affirmations;
    if (usedIndices.current.length >= aff.length) usedIndices.current = [];
    let idx;
    do { idx = Math.floor(Math.random() * aff.length); }
    while (usedIndices.current.includes(idx));
    usedIndices.current.push(idx);
    return { text: aff[idx], idx };
  }, []);

  const handleThemeSelect = useCallback((theme) => {
    setSelectedTheme(theme);
    usedIndices.current = [];
    setBgIndex(0);
    const next = getRandomAffirmation(theme);
    setCurrentAffirmation(next.text);
    setCurrentAffirmationIdx(next.idx);
    setShowGallery(false);
    setFadeIn(false);
    setTimeout(() => setFadeIn(true), 50);
  }, [getRandomAffirmation]);

  const handleNextAffirmation = useCallback(() => {
    if (!selectedTheme || allImages.length === 0) return;
    setFadeIn(false);
    setTimeout(() => {
      setBgIndex(prev => (prev + 1) % allImages.length);
      const next = getRandomAffirmation(selectedTheme);
      setCurrentAffirmation(next.text);
      setCurrentAffirmationIdx(next.idx);
      setAnimKey(prev => prev + 1);
      setFadeIn(true);
    }, 300);
  }, [selectedTheme, getRandomAffirmation, allImages]);

  const handleBack = useCallback(() => {
    if (showGallery) { setShowGallery(false); return; }
    setFadeIn(false);
    setTimeout(() => { setSelectedTheme(null); setCurrentAffirmation(''); }, 200);
  }, [showGallery]);

  const handleUploadPhoto = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTheme) return;
    const current = userPhotos[selectedTheme.id] || [];
    if (current.length >= MAX_USER_PHOTOS) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        // Resize to save localStorage space
        const canvas = document.createElement('canvas');
        const maxW = 800;
        const scale = Math.min(1, maxW / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);

        const updated = { ...userPhotos, [selectedTheme.id]: [...current, dataUrl] };
        saveUserPhotos(updated);
        setUserPhotos(updated);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [selectedTheme, userPhotos]);

  const handleDeletePhoto = useCallback((photoIndex) => {
    if (!selectedTheme) return;
    const current = [...(userPhotos[selectedTheme.id] || [])];
    current.splice(photoIndex, 1);
    const updated = { ...userPhotos, [selectedTheme.id]: current };
    saveUserPhotos(updated);
    setUserPhotos(updated);
    // Reset bg index if needed
    const totalImages = selectedTheme.images.length + current.length;
    if (bgIndex >= totalImages) setBgIndex(0);
  }, [selectedTheme, userPhotos, bgIndex]);

  useEffect(() => {
    if (isOpen) {
      setSelectedTheme(null);
      setCurrentAffirmation('');
      setFadeIn(false);
      setShowGallery(false);
      setUserPhotos(getUserPhotos());
      // Blur any lingering focused input (e.g. keyboard left open from a
      // previous screen). Prevents the Samsung/Android keyboard from
      // covering the panel content and clipping the theme labels.
      try {
        if (typeof document !== 'undefined' && document.activeElement && document.activeElement.blur) {
          document.activeElement.blur();
        }
      } catch { /* noop */ }
    }
  }, [isOpen]);

  // Auto-play helpers: stop current audio + scheduled advance
  const stopAuto = useCallback(() => {
    if (autoAudioRef.current) { try { autoAudioRef.current.pause(); } catch {} autoAudioRef.current = null; }
    if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = null; }
  }, []);

  // Auto-play current affirmation's clip when voiceMode is set (non sans-voix)
  // and optionally advance after `intervalSec` seconds.
  useEffect(() => {
    stopAuto();
    if (!selectedTheme || showGallery || showSessionAudio || autoPlayMode) return;
    const { voiceMode, intervalSec } = settings || {};
    if (!voiceMode || voiceMode === 'sans-voix') {
      // Sans voix: only auto-advance if intervalSec > 0
      if (intervalSec && intervalSec > 0) {
        autoTimerRef.current = setTimeout(() => handleNextAffirmation(), intervalSec * 1000);
      }
      return;
    }
    const clip = getClip(currentAffirmationIdx, voiceMode);
    const src = clip?.audio_url || clip?.url;
    if (src) {
      const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}${src}`);
      a.volume = 1;
      autoAudioRef.current = a;
      const advance = () => {
        if (intervalSec && intervalSec > 0) {
          autoTimerRef.current = setTimeout(() => handleNextAffirmation(), intervalSec * 1000);
        }
      };
      a.onended = advance;
      a.play().catch(() => {});
    } else if (intervalSec && intervalSec > 0) {
      // No clip: just wait interval then advance
      autoTimerRef.current = setTimeout(() => handleNextAffirmation(), intervalSec * 1000);
    }
    return stopAuto;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTheme, currentAffirmationIdx, settings, showGallery, showSessionAudio, autoPlayMode, aClips]);

  // Stop everything on close
  useEffect(() => { if (!isOpen) stopAuto(); }, [isOpen, stopAuto]);

  // The "Féminine" pill is hidden from EVERYONE in the main voice selector
  // when the admin flag is off. If a user (admin or not) previously picked
  // 'sensuelle' as their voice mode, silently fall back to 'sans-voix' so
  // playback doesn't reference an unselectable voice.
  useEffect(() => {
    if (settings.voiceMode === 'sensuelle' && !appSettings.feminine_voice_visible) {
      updateSettings({ voiceMode: 'sans-voix' });
    }
  }, [settings.voiceMode, appSettings.feminine_voice_visible, updateSettings]);

  // Auto-activate "ludovic" voice mode the first time a Ludovic clip is detected
  // for the current theme (so admin's pre-recorded voices are heard automatically).
  const autoActivatedRef = useRef(false);
  useEffect(() => {
    if (!selectedTheme) { autoActivatedRef.current = false; return; }
    if (autoActivatedRef.current) return;
    const hasLudovic = Object.keys(aClips || {}).some((k) => k.endsWith('-ludovic'));
    if (hasLudovic && (settings.voiceMode === 'sans-voix' || !settings.voiceMode)) {
      updateSettings({ voiceMode: 'ludovic' });
      autoActivatedRef.current = true;
    }
  }, [selectedTheme, aClips, settings.voiceMode, updateSettings]);

  // Current clip for inline player on the main view (most useful: Ludovic if active)
  const inlineClip = selectedTheme && settings.voiceMode && settings.voiceMode !== 'sans-voix'
    ? getClip(currentAffirmationIdx, settings.voiceMode)
    : null;

  const [keepAwake, toggleKeepAwake] = useKeepAwake('affirmations', isOpen);

  if (!isOpen) return null;

  const currentUserPhotos = selectedTheme ? (userPhotos[selectedTheme.id] || []) : [];
  const canUpload = currentUserPhotos.length < MAX_USER_PHOTOS;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950" data-testid="affirmations-panel">
      <div className="absolute inset-0 bg-black/95" onClick={onClose} />
      <div className="relative z-10 w-full h-full max-w-lg mx-auto flex flex-col" style={{ maxHeight: '100dvh' }}>

        {/* THEME SELECTION */}
        {!selectedTheme && (
          <div className="flex flex-col h-full px-4 pt-12 pb-24 animate-fadeIn">
            <div className="text-center mb-8">
              <button onClick={onClose} className="absolute top-10 right-4 text-white/60 hover:text-white p-2" data-testid="affirmations-close">
                <X size={22} />
              </button>
              <h2 className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Affirmations Positives
              </h2>
              <p className="text-white/50 text-sm mb-3">Choisissez un thème pour commencer</p>
              <div className="flex justify-center mb-3">
                <KeepAwakeToggle keepAwake={keepAwake} onToggle={toggleKeepAwake} testId="keep-awake-affirmations"
                  className="px-4 py-2 rounded-full text-xs font-medium bg-white/10 border border-white/15 text-white/80 hover:bg-white/20" />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button onClick={() => setShowSessionAudio(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium bg-amber-500/15 border border-amber-400/30 text-amber-300 hover:bg-amber-500/25 transition"
                  data-testid="affirmations-session-settings">
                  <SlidersHorizontal className="w-3.5 h-3.5" /> Paramétrer votre séance
                </button>
                <button onClick={() => setShowDownloads(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/25 transition"
                  data-testid="affirmations-downloads-btn">
                  <ImagePlus className="w-3.5 h-3.5" /> Télécharger les affirmations
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 flex-1 content-start overflow-y-auto pb-24">
              {AFFIRMATION_THEMES.map((theme, i) => {
                const Icon = ICONS[theme.icon];
                const customCount = (userPhotos[theme.id] || []).length;
                return (
                  <button key={theme.id} type="button" onClick={() => handleThemeSelect(theme)}
                    inputMode="none"
                    className="relative overflow-hidden rounded-2xl border border-white/10 p-5 text-left transition-transform duration-300 active:scale-95 hover:border-white/25 cursor-pointer min-h-[140px] flex flex-col"
                    style={{
                      background: `linear-gradient(135deg, ${theme.color}26, ${theme.color}10)`,
                      WebkitTransform: 'translateZ(0)',
                      transform: 'translateZ(0)',
                      isolation: 'isolate',
                    }}
                    data-testid={`theme-${theme.id}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 flex-shrink-0"
                      style={{ background: `${theme.color}33` }}>
                      <Icon size={20} style={{ color: theme.color }} />
                    </div>
                    <p className="text-white font-medium text-sm leading-tight">{theme.label}</p>
                    <p className="text-white/50 text-xs mt-1">
                      {theme.affirmations.length} affirmations
                      {customCount > 0 && <span className="text-amber-400/70"> +{customCount} photos</span>}
                    </p>
                    {isAdmin && (
                      <AffirmationFolderUploader
                        theme={theme}
                        onDone={() => { /* refreshed via panel re-open if needed */ }}
                      />
                    )}
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute -top-8 -right-8 w-20 h-20 rounded-full"
                      style={{
                        background: `radial-gradient(circle, ${theme.color}40 0%, transparent 70%)`,
                        opacity: 0.45,
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* AFFIRMATION DISPLAY */}
        {selectedTheme && !showGallery && (
          <div className="relative flex flex-col h-full" data-testid="affirmation-display">
            {allImages.map((img, i) => (
              <div key={`${selectedTheme.id}-bg-${i}`}
                className="absolute inset-0 bg-cover bg-center transition-opacity duration-700"
                style={{ backgroundImage: `url(${img})`, opacity: i === bgIndex ? 1 : 0 }} />
            ))}
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />

            <div className="relative z-10 flex flex-col h-full px-6 pt-12 pb-24">
              <div className="flex items-center justify-between mb-4">
                <button onClick={handleBack} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="affirmation-back">
                  <ArrowLeft size={20} />
                  <span className="text-sm">Retour</span>
                </button>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowSessionAudio(true)}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-amber-400/40 text-amber-300 hover:border-amber-300 hover:bg-amber-500/10 transition-colors"
                    data-testid="affirmation-session-settings">
                    <SlidersHorizontal size={14} />
                    Paramétrer
                  </button>
                  <button onClick={() => setShowGallery(true)}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/20 text-white/70 hover:border-white/40 hover:text-white transition-colors"
                    data-testid="gallery-btn">
                    <Camera size={14} />
                    Mes photos
                  </button>
                  <span className="text-xs px-3 py-1 rounded-full border border-white/20 text-white/70">
                    {selectedTheme.label}
                  </span>
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center px-2">
                <p key={animKey}
                  className="text-center text-white font-medium leading-relaxed"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 'clamp(1.4rem, 5vw, 2rem)',
                    textShadow: '0 2px 20px rgba(0,0,0,0.7), 0 0 40px rgba(0,0,0,0.5)',
                    opacity: fadeIn ? 1 : 0,
                    transform: fadeIn ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
                    transition: 'opacity 0.5s ease, transform 0.5s ease'
                  }}
                  data-testid="affirmation-text">
                  "{currentAffirmation}"
                </p>
              </div>

              <div className="flex flex-col items-center gap-3 mt-4">
                {/* Inline audio player when a clip is active for current affirmation */}
                {inlineClip && (
                  <div className="w-full bg-slate-900/85 border border-amber-400/40 rounded-2xl p-3 flex items-center gap-3"
                    style={{ transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', isolation: 'isolate' }}
                    data-testid="aff-inline-player">
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Volume2 className="w-4 h-4 text-amber-400" />
                      <span className="text-amber-200 text-[11px] font-medium">
                        {settings.voiceMode === 'ludovic' ? 'Voix de Ludovic' : 'Ma voix'}
                      </span>
                    </div>
                    <audio
                      controls
                      preload="metadata"
                      src={`${process.env.REACT_APP_BACKEND_URL}${inlineClip.url}`}
                      className="flex-1 block"
                      style={{ height: '40px', backgroundColor: '#0f172a', borderRadius: '6px', minWidth: 0 }}
                      data-testid="aff-inline-audio"
                    />
                  </div>
                )}

                {/* Quick voice toggles (mobile-friendly access to settings) */}
                <div className="flex flex-wrap items-center justify-center gap-1.5">
                  {[
                    { id: 'sans-voix', label: '🤫 Texte', tip: 'Texte seul', alwaysVisible: true },
                    { id: 'ludovic', label: '🎙 Ludovic', tip: 'Voix de Ludovic', alwaysVisible: true },
                    { id: 'sensuelle', label: '💕 Féminine', tip: 'Voix féminine IA', alwaysVisible: false },
                    { id: 'mon-choix', label: '🌟 Ma voix', tip: 'Mes enregistrements', alwaysVisible: true },
                  ]
                    // The AI "Féminine" pill is hidden from EVERYONE (including
                    // admin) until the admin explicitly enables it via the
                    // visibility toggle in the "Voix Féminine (Admin)" card.
                    // Admins still manage/test the voice from that card.
                    .filter((opt) => opt.alwaysVisible || appSettings.feminine_voice_visible)
                    .map((opt) => {
                    const active = (settings.voiceMode || 'sans-voix') === opt.id;
                    return (
                      <button key={opt.id} onClick={() => updateSettings({ voiceMode: opt.id })}
                        className={`text-[10px] px-2.5 py-1 rounded-full border transition ${active ? 'bg-amber-500 text-slate-900 border-amber-400 font-semibold' : 'bg-slate-900/60 text-white/70 border-white/15 hover:bg-slate-800'}`}
                        title={opt.tip}
                        data-testid={`aff-quick-voice-${opt.id}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                  <button onClick={() => setShowInfo(true)}
                    className="text-[10px] flex items-center gap-1 px-2.5 py-1 rounded-full border border-white/15 text-white/70 bg-slate-900/60 hover:bg-slate-800"
                    data-testid="affirmation-info-btn">
                    <Info size={10} /> Infos
                  </button>
                </div>

                {/* Admin: bulk-generate sensual voice via OpenAI TTS */}
                {isAdmin && selectedTheme && (
                  <SensualVoiceGenerator
                    theme={selectedTheme}
                    onDone={() => clipsApi.refresh && clipsApi.refresh()}
                    visible={appSettings.feminine_voice_visible}
                    onVisibilityChange={async (v) => {
                      try {
                        await updateAppSettings({ feminine_voice_visible: v });
                        toast.success(v ? 'Voix Féminine mise dans l\'interface utilisateur' : 'Voix Féminine retirée de l\'interface utilisateur');
                      } catch (e) {
                        toast.error('Impossible de mettre à jour le réglage');
                      }
                    }}
                  />
                )}

                {/* Admin: warn about orphan clips that block re-uploads */}
                {isAdmin && (
                  <OrphanClipsBanner
                    onUnlock={() => clipsApi.refresh && clipsApi.refresh()}
                  />
                )}

                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button onClick={handleNextAffirmation}
                    className="flex items-center gap-3 px-6 py-3 rounded-full font-semibold text-base transition-all duration-300 hover:scale-105 active:scale-95"
                    style={{
                      background: `linear-gradient(135deg, ${selectedTheme.color}, ${selectedTheme.color}cc)`,
                      color: '#fff',
                      boxShadow: `0 4px 30px ${selectedTheme.color}40`
                    }}
                    data-testid="next-affirmation-btn">
                    <RefreshCw size={18} />
                    Être plus positif
                  </button>
                  <button onClick={() => setAutoPlayMode(settings.voiceMode || 'sans-voix')}
                    className="flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border border-amber-400/50 bg-black/30 text-amber-300 hover:bg-amber-500/15 transition"
                    data-testid="full-session-btn">
                    <PlayCircle size={18} /> Séance complète
                  </button>
                </div>
                <p className="text-white/30 text-xs">{bgIndex + 1} / {allImages.length}</p>
              </div>
            </div>
          </div>
        )}

        {/* PHOTO GALLERY / MANAGER */}
        {selectedTheme && showGallery && (
          <div className="flex flex-col h-full px-4 pt-6 pb-24 animate-fadeIn" data-testid="photo-gallery">
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => setShowGallery(false)} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="gallery-back">
                <ArrowLeft size={20} />
                <span className="text-sm">Retour</span>
              </button>
              <h3 className="text-white font-semibold text-base">{selectedTheme.label}</h3>
            </div>

            <div className="overflow-y-auto flex-1">
              {/* Default photos */}
              <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3">Photos par défaut</p>
              <div className="grid grid-cols-3 gap-2 mb-6">
                {selectedTheme.images.map((img, i) => (
                  <div key={`default-${i}`} className="aspect-[3/4] rounded-xl overflow-hidden border border-white/10">
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ))}
              </div>

              {/* User photos */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-white/50 text-xs font-semibold uppercase tracking-wider">
                  Mes photos ({currentUserPhotos.length}/{MAX_USER_PHOTOS})
                </p>
                {canUpload && (
                  <button onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 transition-colors"
                    data-testid="upload-photo-btn">
                    <ImagePlus size={14} />
                    Ajouter
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUploadPhoto} data-testid="photo-file-input" />

              {currentUserPhotos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 border border-dashed border-white/15 rounded-2xl">
                  <Camera size={32} className="text-white/20 mb-3" />
                  <p className="text-white/30 text-sm mb-1">Aucune photo ajoutée</p>
                  <p className="text-white/20 text-xs">Ajoutez jusqu'à 5 photos personnalisées</p>
                  {canUpload && (
                    <button onClick={() => fileRef.current?.click()}
                      className="mt-4 flex items-center gap-2 text-sm px-5 py-2.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 transition-colors"
                      data-testid="upload-photo-empty-btn">
                      <ImagePlus size={16} />
                      Choisir une photo
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {currentUserPhotos.map((photo, i) => (
                    <div key={`user-${i}`} className="aspect-[3/4] rounded-xl overflow-hidden border border-amber-500/20 relative group">
                      <img src={photo} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => handleDeletePhoto(i)}
                        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-500/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity active:opacity-100"
                        style={{ opacity: undefined }}
                        data-testid={`delete-photo-${i}`}>
                        <Trash2 size={14} className="text-white" />
                      </button>
                      {/* Always visible delete on mobile */}
                      <div className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-500/90 flex items-center justify-center sm:hidden"
                        onClick={() => handleDeletePhoto(i)}>
                        <Trash2 size={14} className="text-white" />
                      </div>
                    </div>
                  ))}
                  {canUpload && (
                    <button onClick={() => fileRef.current?.click()}
                      className="aspect-[3/4] rounded-xl border-2 border-dashed border-white/15 flex flex-col items-center justify-center gap-1 hover:border-amber-500/40 transition-colors"
                      data-testid="upload-photo-grid-btn">
                      <ImagePlus size={20} className="text-white/30" />
                      <span className="text-white/30 text-xs">Ajouter</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease forwards; }
      `}</style>

      <SessionAudioPanel
        isOpen={showSessionAudio}
        onClose={() => setShowSessionAudio(false)}
        section="affirmations"
        isAdmin={isAdmin}
        affirmationsSettings={settings}
        onAffirmationsSettingsChange={updateSettings}
        affirmationsTheme={selectedTheme}
        affirmationsClipsApi={clipsApi}
      />

      {/* Auto-player */}
      {autoPlayMode && selectedTheme && (
        <AffirmationsAutoPlayer
          theme={selectedTheme}
          clipsMap={aClips}
          voiceMode={autoPlayMode}
          intervalSec={settings.intervalSec}
          onClose={() => setAutoPlayMode(null)}
        />
      )}

      {/* Info modal: why & how */}
      {showInfo && (
        <div className="fixed inset-0 z-[260] flex items-center justify-center" data-testid="aff-info-modal">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setShowInfo(false)} />
          <div className="relative z-10 w-full max-w-md mx-4 max-h-[90dvh] overflow-y-auto rounded-3xl bg-gradient-to-b from-slate-900 to-slate-950 border border-amber-500/25 p-6 space-y-4 text-sm leading-relaxed">
            <div className="flex items-center justify-between">
              <h3 className="text-amber-300 font-semibold text-lg flex items-center gap-2">
                <Info className="w-5 h-5" /> Affirmations positives
              </h3>
              <button onClick={() => setShowInfo(false)} className="p-1 rounded-full hover:bg-white/10 text-white/60" data-testid="aff-info-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <section>
              <h4 className="text-amber-200 font-medium mb-1">✨ Les bienfaits</h4>
              <p className="text-white/75 text-[13px]">
                Les affirmations positives reprogramment progressivement le dialogue intérieur. Répétées
                régulièrement, elles réduisent le stress, renforcent la confiance, apaisent l'anxiété et
                améliorent l'humeur. Des études en neurosciences montrent que cette pratique active les
                circuits cérébraux de la récompense et de la valorisation de soi.
              </p>
            </section>
            <section>
              <h4 className="text-amber-200 font-medium mb-1">🕒 Fréquence d'écoute idéale</h4>
              <ul className="text-white/75 text-[13px] list-disc pl-5 space-y-0.5">
                <li><b>Matin</b> au réveil (5–10 min) pour ancrer la journée.</li>
                <li><b>Soir</b> avant de dormir (10 min) pour laisser le subconscient les intégrer.</li>
                <li>Idéalement <b>21 jours</b> consécutifs pour créer un nouvel ancrage durable.</li>
              </ul>
            </section>
            <section>
              <h4 className="text-amber-200 font-medium mb-1">🎙 Enregistrer votre propre voix</h4>
              <ol className="text-white/75 text-[13px] list-decimal pl-5 space-y-1">
                <li>Cherchez un endroit calme, sans écho.</li>
                <li>Cliquez sur <b>Enregistrer micro</b> sous "La voix de mon choix" — le navigateur vous demandera l'accès au micro, acceptez.</li>
                <li>Lisez l'affirmation avec douceur et conviction, à voix haute.</li>
                <li>Cliquez sur <b>Stop</b> : votre voix est encodée en MP3 et sauvegardée instantanément.</li>
                <li>Vous pouvez réécouter, réenregistrer ou supprimer à tout moment.</li>
              </ol>
              <p className="text-white/55 text-[12px] mt-2 italic">
                Astuce : entendre sa propre voix prononcer ces affirmations renforce considérablement leur
                impact émotionnel.
              </p>
            </section>
          </div>
        </div>
      )}

      {/* Downloads modal: PDF/TXT per category + combined */}
      {showDownloads && (
        <div className="fixed inset-0 z-[260] flex items-center justify-center" data-testid="downloads-modal">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setShowDownloads(false)} />
          <div className="relative z-10 w-full max-w-md mx-4 max-h-[92dvh] overflow-y-auto rounded-3xl bg-gradient-to-b from-slate-900 to-slate-950 border border-emerald-500/25 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-emerald-300 font-semibold text-lg">📥 Téléchargements</h3>
              <button onClick={() => setShowDownloads(false)} className="p-1 rounded-full hover:bg-white/10 text-white/60" data-testid="downloads-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-white/65 text-xs leading-relaxed">
              Emportez vos affirmations partout : PDF imprimables pour les afficher chez vous,
              TXT pour les copier dans une autre app. Idéal pour la pratique des 21 jours.
            </p>

            {/* Pack complet */}
            <a href="/affirmations/affirmations-hypnose-cle.zip" download
              className="block rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/15 to-amber-600/10 p-4 hover:from-amber-500/25 transition"
              data-testid="dl-pack">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-amber-200 font-semibold text-sm">📦 Pack complet (ZIP)</p>
                  <p className="text-white/55 text-[11px] mt-0.5">6 thèmes · PDF + TXT · ~35 Ko</p>
                </div>
                <span className="text-amber-300 text-xl">↓</span>
              </div>
            </a>

            {/* PDF combiné */}
            <a href="/affirmations/affirmations-toutes.pdf" download
              className="block rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-3 hover:bg-emerald-500/20 transition"
              data-testid="dl-all-pdf">
              <p className="text-emerald-200 font-medium text-sm">📕 Toutes les affirmations (PDF unique)</p>
              <p className="text-white/50 text-[11px] mt-0.5">6 thèmes · 17 Ko · imprimable</p>
            </a>

            {/* Liste par thème */}
            <div className="space-y-2 pt-2 border-t border-white/5">
              <p className="text-white/50 text-[10px] uppercase tracking-wider">Par thème</p>
              {[
                { id: 'bienetre', label: 'Bien-être' },
                { id: 'confiance', label: 'Confiance en soi' },
                { id: 'sport', label: 'Sport' },
                { id: 'determination', label: 'Détermination' },
                { id: 'beaute', label: 'Se sentir beau' },
                { id: 'guerir', label: 'Guérir' },
              ].map((t) => (
                <div key={t.id} className="rounded-xl border border-white/10 bg-white/[0.04] p-2.5 flex items-center gap-2">
                  <p className="text-white/85 text-sm flex-1">{t.label}</p>
                  <a href={`/affirmations/affirmations-${t.id}.pdf`} download
                    className="text-[11px] px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/25 transition"
                    data-testid={`dl-pdf-${t.id}`}>PDF</a>
                  <a href={`/affirmations/affirmations-${t.id}.txt`} download
                    className="text-[11px] px-2.5 py-1 rounded-full bg-slate-700/60 border border-white/10 text-white/70 hover:bg-slate-600 transition"
                    data-testid={`dl-txt-${t.id}`}>TXT</a>
                </div>
              ))}
            </div>

            <p className="text-white/40 text-[10px] italic text-center pt-2">
              hypnoseclee.com · À répéter matin et soir pendant 21 jours
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffirmationsPanel;
